import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { ReactElement } from 'react';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import Button from '@ui/Button';
import CircularLoader from '../LoadingIndicators/CircularLoader';

const useStyles = makeStyles()(theme => ({
  root: {
    '& strong, & em': {
      fontWeight: 'bold',
    },
    padding: theme.spacing(5),
  },
  paragraph: {
    marginTop: theme.spacing(1),
    color: palette.gray_75,
  },
  cancel: {
    '&:hover, &.Mui-focusVisible': {
      color: theme.palette.primary.main,
    },
  },
}));

type BodySection = string | ReactElement;

interface DestructiveDialogContentProps {
  title: string;
  body?: BodySection | BodySection[];
  error?: string;
  onCancel: () => void;
  actionText: string;
  onAction: () => void;
  actionsDisabled?: boolean;
  isLoading?: boolean;
  doNotShowAgainCTA?: boolean; // Destructive modals with do not show again logic requires less padding above CTA
  /** Used to move keyboard focus into the modal on open */
  cancelButtonRef?: React.RefObject<HTMLButtonElement>;
}

const DestructiveDialogContent = ({
  title,
  body = [],
  error,
  onCancel,
  actionText,
  onAction,
  actionsDisabled,
  isLoading,
  doNotShowAgainCTA,
  cancelButtonRef,
}: DestructiveDialogContentProps) => {
  const { classes } = useStyles();

  const paragraphs = Array.isArray(body) ? body : [body];

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onAction();
  };

  return (
    <form className={classes.root} onSubmit={onSubmit}>
      <Box>
        <Typography variant="h4" bold>
          {title}
        </Typography>
        {paragraphs.map((p, index) => (
          <Typography
            variant="pMd"
            // eslint-disable-next-line react/no-array-index-key
            key={`paragraph-${index}`}
            className={classes.paragraph}
          >
            {p}
          </Typography>
        ))}
      </Box>
      {error !== undefined && (
        <Box>
          <Typography role="alert" color="error" variant="pMd">
            {error}
          </Typography>
        </Box>
      )}
      <Box mt={doNotShowAgainCTA ? 4 : 5} display="flex" alignItems={'center'}>
        <Box width="100%" mr={0.5}>
          <Button
            variant="secondary"
            ref={cancelButtonRef}
            fullWidth
            tabIndex={0}
            onClick={onCancel}
            disabled={actionsDisabled}
            size="medium"
            data-e2e="action-destructive-dialog-content-cancel"
          >
            Cancel
          </Button>
        </Box>
        <Box width="100%" ml={0.5}>
          <Button
            variant="destructive"
            fullWidth
            type="submit"
            disabled={actionsDisabled}
            loading={actionsDisabled}
            size="medium"
            data-e2e="action-destructive-dialog-content"
          >
            {isLoading ? (
              <CircularLoader size={24} color="secondary" />
            ) : (
              actionText
            )}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default DestructiveDialogContent;
